import React, { useState, useEffect } from 'react';
import translate from "../../../config/translations/translate";
import {
  Table,
  UxTooltip
} from 'botbit-ui-components';
import DashboardPeriodSelector from '../../dashboardV2/dashboardPeriodSelector/DashboardPeriodSelector';
import { getQRScansLeaderboardByStore, getQRScansLeaderboardByStoreForEmployee } from '../../../api/reviewLeaderboardApi';
import { QRScanLeaderboardIcon, QRScanAddEmployeeIcon, QRScanDownloadIcon } from '../../dashboardV2/DashboardV2ContainerIcons';
import './ReviewLeaderboardStoreView.scss';
import { DashboardStoreSelector } from '../../dashboardV2/dashboardStoreSelector/DashboardStoreSelector';
import { useHistory } from 'react-router-dom';
import withEventTracking, { trackers } from '../../../config/withEventTracking';
import JSZip from 'jszip';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const ReviewLeaderboardStoreView = ({ storeId, storeName, t, showStoreSelector, stores, selectedStore, handleStoreChange, employeeView = false, showPeriodSelector = true, showEmployeeAdminButton = true, trackEvent }) => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentOrder, setCurrentOrder] = useState({ name: 'position', direction: 'asc' });
  const [localSelectedPeriod, setLocalSelectedPeriod] = useState({
    value: 'LAST_7_DAYS'
  });
  const [downloadingAll, setDownloadingAll] = useState(false);
  const history = useHistory();

  const modalTimePeriodOptions = [
    { value: 'LAST_7_DAYS', label: t('timePeriod.last7Days') },
    { value: 'LAST_15_DAYS', label: t('timePeriod.last15Days') },
    { value: 'THIS_MONTH', label: t('timePeriod.thisMonth') },
    { value: 'PAST_MONTH', label: t('timePeriod.pastMonth') },
    { value: 'LAST_3_MONTHS', label: t('timePeriod.last3Months') },
    { value: 'LAST_6_MONTHS', label: t('timePeriod.last6Months') },
    { value: 'LAST_12_MONTHS', label: t('timePeriod.last12Months') },
  ];

  useEffect(() => {
    if (storeId) {
      fetchLeaderboardData();
      
      // Track Review Leaderboard location view event
      trackEvent('Reviews_Leaderboard_location_view', {
        storeId: storeId
      }, false, [trackers.MIXPANEL, trackers.INTERCOM]);
    }
  }, [storeId, localSelectedPeriod]);

  const handleLocalPeriodChange = (period) => {
    setLocalSelectedPeriod(period);
  };

  const fetchLeaderboardData = async () => {
    if (!storeId) return;

    setLoading(true);
    setError(null);

    try {
      if (!employeeView) {
        const response = await getQRScansLeaderboardByStore(storeId, { timePeriod: localSelectedPeriod });
        setLeaderboardData(response.data.items || []);
      }
      else {
        const response = await getQRScansLeaderboardByStoreForEmployee();
        setLeaderboardData(response.data.items || []);
      }
    } catch (error) {
      console.error("Error fetching QR Scans Leaderboard data:", error);
      setError(t('errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async (page, order, filters) => {
    const orderChanged = order && (currentOrder.name !== order.name || currentOrder.direction !== order.direction);

    if (orderChanged && order) {
      setCurrentOrder(order);
    }

    const sortName = order ? order.name : currentOrder.name;
    const sortDirection = order ? order.direction : currentOrder.direction;

    const sortedData = [...leaderboardData].sort((a, b) => {
      const valueA = a[sortName];
      const valueB = b[sortName];

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      return sortDirection === 'asc'
        ? valueA - valueB
        : valueB - valueA;
    });

    return {
      data: {
        data: {
          items: sortedData,
          totalSize: sortedData.length,
          pageSize: sortedData.length
        }
      }
    };
  };

  const handleTableChange = (type, { sortField, sortOrder }) => {
    if (type === 'sort') {
      setCurrentOrder({
        name: sortField,
        direction: sortOrder
      });
    }
  };

  const handleDownloadQR = async (qrLink, employeeName = '', employeeLastName = '', storeName = '') => {
    if (!qrLink) return;

    const fullUrl = qrLink.startsWith('http') ? qrLink : `https://${qrLink}`;

    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(fullUrl)}`;

    // Helper function to normalize accented characters
    const normalizeText = (text) => {
      return text
        .normalize('NFD') // Decompose characters with their accents
        .replace(/[\u0300-\u036f]/g, '') // Remove accent marks
        .replace(/\s+/g, '-') // Convert spaces to hyphens
        .replace(/[^A-Za-z0-9-]/g, ''); // Remove other special characters
    };

    // Generate a custom filename based on employee info and store name
    const cleanName = normalizeText(employeeName) || 'employee';
    const cleanLastName = normalizeText(employeeLastName) || '';
    const cleanStoreName = normalizeText(storeName) || 'store';

    const filename = cleanLastName
      ? `${cleanName}-${cleanLastName}-${cleanStoreName}.png`
      : `${cleanName}-${cleanStoreName}.png`;

    try {
      // Fetch the QR code image
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename; // Use the custom filename
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading QR code:', error);
      // Fallback to opening in new tab if download fails
      window.open(qrCodeUrl, '_blank');
    }
  };

  const handleDownloadAllQRs = async () => {
    if (!leaderboardData || leaderboardData.length === 0) {
      MySwal.fire({
        type: "warning",
        text: t("noEmployeesToDownload"),
        confirmButtonText: t("close"),
        confirmButtonColor: "#ff7043"
      });
      return;
    }

    setDownloadingAll(true);

    try {
      const zip = new JSZip();

      // Helper function to normalize accented characters
      const normalizeText = (text) => {
        return text
          .normalize('NFD') // Decompose characters with their accents
          .replace(/[\u0300-\u036f]/g, '') // Remove accent marks
          .replace(/\s+/g, '_') // Convert spaces to underscores
          .replace(/[^A-Za-z0-9_-]/g, '_'); // Remove other special characters
      };

      // Process all employees in parallel
      const downloadPromises = leaderboardData.map(async (employee, index) => {
        if (!employee.qrLink) return null;

        try {
          const fullUrl = employee.qrLink.startsWith('http') ? employee.qrLink : `https://${employee.qrLink}`;
          const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(fullUrl)}`;

          // Generate filename: nombre_apellido.png
          const cleanName = normalizeText(employee.adminName || 'employee');
          const cleanLastName = normalizeText(employee.adminLastName || '');
          const fileName = cleanLastName 
            ? `${cleanName}_${cleanLastName}.png`
            : `${cleanName}.png`;

          // Fetch the QR code image
          const response = await fetch(qrCodeUrl);
          if (response.ok) {
            const blob = await response.blob();
            zip.file(fileName, blob);
            return { success: true, fileName };
          } else {
            console.error(`Error fetching QR for ${employee.adminName || 'employee'}: ${response.status}`);
            return { success: false, error: `HTTP ${response.status}` };
          }
        } catch (error) {
          console.error(`Error processing QR for ${employee.adminName || 'employee'}:`, error);
          return { success: false, error: error.message };
        }
      });

      // Wait for all downloads to complete
      await Promise.all(downloadPromises);

      // Generate and download the ZIP
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = window.URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `qrs_employees_${storeName ? storeName : 'store'}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show success message
        MySwal.fire({
          type: "success",
          text: t("downloadSuccess"),
          confirmButtonText: t("close"),
          confirmButtonColor: "#ff7043"
        });

    } catch (error) {
      console.error('Error downloading all QRs:', error);
      MySwal.fire({
        type: "error",
        text: t("downloadError"),
        confirmButtonText: t("close"),
        confirmButtonColor: "#ff7043"
      });
    } finally {
      setDownloadingAll(false);
    }
  };

  const columns = [
    {
      dataField: 'position',
      title: '#',
      size: 15,
      sortable: true,
      formatter: (cellValue) => (
        <span className="leaderboard-position">#{cellValue}</span>
      ),
    },
    {
      dataField: 'adminName',
      title: (
        <div className="column-with-tooltip">
          {t('columns.name')}
          <UxTooltip
            message={t('columns.name.tooltip.content')}
            position="top"
          />
        </div>
      ),
      size: 35,
      sortable: true,
      formatter: (cellValue, row) => {
        const fullName = `${cellValue || ''} ${row.adminLastName || ''}`.trim();
        return <span className="employee-name">{fullName}</span>;
      },
    },
    {
      dataField: 'scanCount',
      title: (
        <div className="column-with-tooltip">
          {t('columns.qrScans')}
          <UxTooltip
            message={t('columns.qrScans.tooltip.content')}
            position="top"
          />
        </div>
      ),
      formatter: (cellValue) => (
        <span className="qr-scans-value">{cellValue}</span>
      ),
      size: 35,
      sortable: true,
      tooltip: {
        title: t('columns.qrScans'),
        content: t('columns.qrScans.tooltip.content')
      }
    },
    {
      dataField: 'qrLink',
      title: t('columns.actions'),
      size: 15,
      formatter: (cellValue, row) => (
        <span>

          <span
            className="download-qr-button"
            onClick={() => handleDownloadQR(cellValue, row.adminName, row.adminLastName, storeName)}
          >

            {t('buttons.downloadQR')}
            <QRScanDownloadIcon />
          </span>
        </span>
      ),
    }
  ];

  return (
    <div>
      <div className="qr-scans-leaderboard-header">
        <h1 className="store-name">{storeName || t('defaultStoreName')}</h1>
      </div>

      <div className="qr-scans-leaderboard-content">
        <div className="qr-scans-section-header">
          <div className="qr-icon-title">
            <div className="qr-icon">
              <QRScanLeaderboardIcon />
            </div>
            <div className="title-description">
              <h2>{t('title')}</h2>
              <p>{t('description')} {t('rankingLink')}</p>
            </div>
            <div className="download-button-container">
              {/* Download All QRs Button */}
              {!loading && !error && leaderboardData.length > 0 && (
              <button
                className="download-all-qrs-button"
                onClick={handleDownloadAllQRs}
                disabled={downloadingAll}
              >
                {downloadingAll ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    {t('buttons.downloadingAll')}
                  </>
                ) : (
                  <>
                    <i className="fas fa-download"></i>
                    {t('buttons.downloadAll')}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        </div>
        <div className="qr-scans-table-section">
          <div className="qr-scans-table-header">
            <h3>{t('tableTitle')}</h3>
            <div className="info-tooltip">
              <UxTooltip
                title={t('columns.qrScans.tooltip.title')}
                content={t('columns.qrScans.tooltip.content')}
              />
            </div>
            {showStoreSelector && (
              <DashboardStoreSelector
                stores={stores}
                selectedStore={selectedStore}
                handleStoreChange={handleStoreChange}
                t={t}
              />
            )}


            {showPeriodSelector && (
              <div className="period-selector-container">
                <DashboardPeriodSelector
                  timePeriodOptions={modalTimePeriodOptions}
                  selectedPeriod={localSelectedPeriod}
                  handlePeriodChange={handleLocalPeriodChange}
                  t={t}
                  useDisabledDays={false}
                />
              </div>
            )}
          </div>

          <div className="qr-scans-table-container">
            {loading && (
              <div className="loading-message">{t('loading')}</div>
            )}

            {error && (
              <div className="error-message">{error}</div>
            )}

            {!loading && !error && leaderboardData.length === 0 && (
              <div className="empty-state">
                <i className="fas fa-users empty-state-icon"></i>
                <h3>{t("emptyState.title")}</h3>
                <h3>{t("emptyState.description")}</h3>
                <button
                  className="btn-new-admin"
                  onClick={() => history.push("/employee/new")}
                >
                  <i className="fas fa-user-plus"></i> {t("emptyState.newEmployee")}
                </button>
              </div>
            )}

            {!loading && !error && leaderboardData.length > 0 && (
              <Table
                getData={getTableData}
                columns={columns}
                showMobileColumnTitles={true}
                loading={loading}
                dataPath="data.data.items"
                elementKey="adminId"
                pagination={false}
                filterValues={{}}
                onTableChange={handleTableChange}
                remote={{ sort: true }}
              />
            )}
          </div>
        </div>

        {showEmployeeAdminButton && (
          <div className="qr-scans-footer">
            <button
              className="abm-employees-button"
              onClick={() => history.push("/employee")}
            >
              <QRScanAddEmployeeIcon />
              {t('buttons.abmEmployees')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

ReviewLeaderboardStoreView.translations = {
  title: {
    es: "Tabla QR de posiciones",
    en: "QR Scan Leaderboard"
  },
  description: {
    es: "Mejora el potencial de tus empleados para pedir nuevos QR Scans.",
    en: "Improve your employees' potential to request new QR Scans."
  },
  rankingLink: {
    es: "Aquí puedes ver el ranking en base a los pedidos.",
    en: "Here you can see the ranking based on orders."
  },
  tableTitle: {
    es: "QR Scans por empleado",
    en: "QR Scans per employee"
  },
  locationSelector: {
    label: {
      es: "Tienda",
      en: "Store"
    }
  },
  timePeriod: {
    label: {
      es: "Período de tiempo",
      en: "Time period"
    },
    last7Days: {
      es: "Últimos 7 días",
      en: "Last 7 days"
    },
    last15Days: {
      es: "Últimos 15 días",
      en: "Last 15 days"
    },
    last30Days: {
      es: "Últimos 30 días",
      en: "Last 30 days"
    },
    thisMonth: {
      es: "Este mes",
      en: "This month"
    },
    pastMonth: {
      es: "Mes pasado",
      en: "Last month"
    },
    last3Months: {
      es: "Últimos 3 meses",
      en: "Last 3 months"
    },
    last6Months: {
      es: "Últimos 6 meses",
      en: "Last 6 months"
    },
    last12Months: {
      es: "Últimos 12 meses",
      en: "Last 12 months"
    }
  },
  defaultStoreName: {
    es: "Tienda",
    en: "Store"
  },
  loading: {
    es: "Cargando datos...",
    en: "Loading data..."
  },
  noData: {
    es: "No hay datos disponibles",
    en: "No data available"
  },
  emptyState: {
    title: {
      es: "No hay empleados creados aún",
      en: "No employees created yet"
    },
    description: {
      es: "Para empezar a usar esta funcionalidad, necesitas crear empleados y descargar sus códigos QR.",
      en: "To start using this feature, you need to create employees and download their QR codes."
    },
    newEmployee: {
      es: "Nuevo empleado",
      en: "New employee"
    }
  },
  columns: {
    name: {
      es: "Nombre",
      en: "Name",
      tooltip: {
        content: {
          es: "Nombre del empleado",
          en: "Employee name"
        }
      }
    },
    qrScans: {
      es: "QR Scans",
      en: "QR Scans",
      tooltip: {
        title: {
          es: "QR Scans",
          en: "QR Scans"
        },
        content: {
          es: "Es la cantidad de scans únicos de los QRs de todos los mozos de esta tienda sumados. Esta métrica diferencia por cuenta de Google, asegurando confiabilidad en la unicidad",
          en: "It is the number of unique scans of the QRs of all the waiters in this store added together. This metric differentiates by Google account, ensuring reliability in uniqueness"
        }
      }
    },
    actions: {
      es: "Acciones",
      en: "Actions"
    }
  },
  buttons: {
    downloadQR: {
      es: "Download QR",
      en: "Download QR"
    },
    downloadAll: {
      es: "Descargar todos los QRs",
      en: "Download All QRs"
    },
    downloadingAll: {
      es: "Descargando...",
      en: "Downloading..."
    },
    abmEmployees: {
      es: "Empleados",
      en: "Employees"
    },
    openQR: {
      es: "Abrir",
      en: "Open"
    }
  },
  errors: {
    fetchFailed: {
      es: "Error al cargar los datos. Por favor, intente nuevamente.",
      en: "Failed to load data. Please try again."
    }
  },
  downloadSuccess: {
    es: "QR Codes descargados exitosamente.",
    en: "QR Codes downloaded successfully."
  },
  downloadError: {
    es: "Error al descargar los QR Codes. Por favor, intente nuevamente.",
    en: "Error downloading QR Codes. Please try again."
  },
  noEmployeesToDownload: {
    es: "No hay empleados para descargar QR Codes.",
    en: "No employees to download QR Codes."
  }
};

export default withEventTracking(translate(ReviewLeaderboardStoreView)); 